import { PropertyBedroomType } from "@/enums/property.enum";
import { Property } from "@/interfaces/property.interface";
import { formatNumber } from "@/lib/utils";
import clsx from "clsx";
import { useTranslations } from "next-intl";
import { ClassNameValue } from "tailwind-merge";
import { BathroomIcon, BedIcon, MapIcon, SquareSizeIcon } from "../Icons";
import { P } from "./Heading";

export const PropertyInfoIcons = ({
  property,
  size = "md",
  containerClassName = "space-y-2",
}: {
  property: Property;
  size?: "sm" | "md";
  containerClassName?: ClassNameValue;
}) => {
  const t = useTranslations("shared.words");

  const formatBedrooms = (bedrooms: PropertyBedroomType | string) => {
    if (!bedrooms || bedrooms?.toLowerCase() === "n/a") {
      return {
        value: t("n/a"),
        label: t("type"),
      };
    }
    if (bedrooms === PropertyBedroomType.STUDIO) {
      return {
        value: t("studio"),
        label: t("type"),
      };
    }

    return {
      value: formatNumber(parseInt(property.bedrooms)),
      label: t("beds"),
    };
  };

  const bedroomInfo = formatBedrooms(property.bedrooms);

  const features = [
    {
      icon: (
        <BedIcon
          className={clsx("text-gray-800", {
            "size-5": size == "md",
            "size-4": size == "sm",
          })}
        />
      ),
      ...bedroomInfo,
    },
    {
      icon: (
        <BathroomIcon
          className={clsx("text-gray-800", {
            "size-5": size == "md",
            "size-4": size == "sm",
          })}
        />
      ),
      value:
        property.bathrooms && property.bathrooms.toLowerCase() !== "n/a"
          ? formatNumber(parseInt(property.bathrooms))
          : t("n/a"),
      label: t("baths"),
    },
    {
      icon: (
        <SquareSizeIcon
          className={clsx("text-gray-800", {
            "size-5": size == "md",
            "size-4": size == "sm",
          })}
        />
      ),
      value: `${t("sqft", { value: formatNumber(property.size) })}`,
      label: t("area"),
    },
  ];

  return (
    <div className={clsx(containerClassName)}>
      <div className="flex items-stretch gap-1.5 ">
        <MapIcon
          className={clsx("text-gray-800", {
            "size-5": size == "md",
            "size-4": size == "sm",
          })}
        />
        <P
          className={clsx("leading-6 font-medium text-gray-900 line-clamp-1", {
            "text-sm leading-4": size === "sm",
          })}
        >
          {property.location.pathName}
        </P>
      </div>
      <div className="flex items-center gap-4">
        {features.map((feature, index) => (
          <div key={index} className={"flex items-center gap-4"}>
            <div className="flex items-stretch gap-1.5">
              <span>{feature.icon}</span>
              <div className="flex flex-col">
                <P
                  className={clsx("font-medium leading-6 text-gray-900", {
                    "text-sm leading-4": size === "sm",
                  })}
                >
                  {feature.value}
                </P>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
